import { useState, useEffect, useRef } from 'react';

export default function Hero() {
  const [counts, setCounts] = useState([0, 0, 0, 0]);
  const [hasAnimated, setHasAnimated] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  const stats = [
    { icon: 'ri-book-open-line', value: 500, suffix: '+', label: 'Ders Notu' },
    { icon: 'ri-file-list-3-line', value: 100, suffix: '+', label: 'Online Deneme' },
    { icon: 'ri-user-star-line', value: 200, suffix: '+', label: 'Aktif Öğrenci' },
    { icon: 'ri-trophy-line', value: 95, suffix: '%', label: 'Başarı Oranı', prefix: '%' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          
          stats.forEach((stat, index) => {
            let current = 0;
            const increment = stat.value / 50;
            const timer = setInterval(() => {
              current += increment;
              if (current >= stat.value) {
                current = stat.value;
                clearInterval(timer);
              }
              setCounts(prev => {
                const newCounts = [...prev];
                newCounts[index] = Math.floor(current);
                return newCounts;
              });
            }, 30);
          });
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/470140a41a4d70bd4a72ecff823cb39a.jpg"
          alt="Eğitim Platformu Arka Plan"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-teal-900/70 via-emerald-900/60 to-teal-800/70"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center w-full">
        <div className="space-y-8">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
            <span className="text-white text-sm font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Yapay Zeka Destekli Eğitim Platformu
            </span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            LGS'ye Hazırlık
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-emerald-300">
              Artık Daha Kolay
            </span>
          </h1>

          {/* Description */}
          <p className="text-xl md:text-2xl text-teal-100 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Ders notları, online denemeler, ödev takibi ve yapay zeka destekli kişisel değerlendirme sistemi ile başarıya ulaşın
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
            <a
              href="/auth/login"
              className="px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Hemen Başla
            </a>
            <a
              href="#ozellikler"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg border-2 border-white/30 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Özellikleri Keşfet
            </a>
          </div>

          {/* Stats */}
          <div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-16 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <i className={`${stat.icon} text-4xl text-emerald-300`}></i>
                </div>
                <div className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {stat.prefix === '%' && '%'}
                  {counts[index]}
                  {stat.suffix && stat.suffix !== '%' && stat.suffix}
                </div>
                <div className="text-sm text-teal-200" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <a href="#ozellikler" className="cursor-pointer">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white rounded-full animate-bounce"></div>
          </div>
        </a>
      </div>
    </section>
  );
}
